import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PasswordInput from '../components/common/PasswordInput';
import './StoreClubMembers.css';

interface Animal {
  id: number;
  animal_type: string;
  category: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: number | null;
}

interface OrderItem {
  id: number;
  product_name: string;
  quantity: number;
  line_total: number;
}

interface Order {
  id: number;
  order_number: string;
  order_date: string;
  status: string;
  status_display: string;
  total: number;
  subtotal: number;
  convenience_fee: number;
  pickup_method: string;
  items: OrderItem[];
}

interface StoreClubMember {
  id: number;
  name: string;
  email: string;
  phone: string;
  street_address?: string;
  city: string;
  state: string;
  zip_code?: string;
  joined_date: string;
  is_approved: boolean;
  approved_by?: string;
  approved_at?: string;
  animal_count: number;
  order_count: number;
  total_spent: number;
  distance_miles: number | null;
  drive_time_minutes?: number;
  reminder_days_before?: number;
  weather_alerts_enabled?: boolean;
  animals?: Animal[];
  orders?: Order[];
}

interface Stats {
  total_members: number;
  approved_members: number;
  pending_members: number;
  new_this_month: number;
  orders_this_month: number;
  revenue_this_month: number;
  avg_order_value: number;
}

interface EditFormData {
  name: string;
  email: string;
  phone: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  reminder_days_before: number;
  weather_alerts_enabled: boolean;
  is_approved: boolean;
}

type FilterType = 'all' | 'pending' | 'new' | 'approved';

const StoreClubMembers: React.FC = () => {
  const [members, setMembers] = useState<StoreClubMember[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedMember, setSelectedMember] = useState<StoreClubMember | null>(null);
  const [memberDetail, setMemberDetail] = useState<StoreClubMember | null>(null);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'animals' | 'orders'>('details');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    reminder_days_before: 3,
    weather_alerts_enabled: true,
    is_approved: false,
  });

  // Password management
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  useEffect(() => {
    loadMembers();
    loadStats();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/store-club-members/');
      setMembers(response.data.members || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load Store Club members');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/store-club-members/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadMemberDetail = async (memberId: number) => {
    try {
      const response = await api.get(`/admin/store-club-members/${memberId}/`);
      setMemberDetail(response.data);
    } catch (err) {
      console.error('Error loading member detail:', err);
    }
  };

  // Filter members based on active filter and search term
  const filteredMembers = members.filter(member => {
    // Apply filter
    let matchesFilter = true;
    switch (activeFilter) {
      case 'pending':
        matchesFilter = !member.is_approved;
        break;
      case 'approved':
        matchesFilter = member.is_approved;
        break;
      case 'new':
        // New this month - joined within last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        matchesFilter = new Date(member.joined_date) >= thirtyDaysAgo;
        break;
      default:
        matchesFilter = true;
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        (member.name || '').toLowerCase().includes(search) ||
        (member.email || '').toLowerCase().includes(search) ||
        (member.phone || '').includes(search) ||
        (member.city || '').toLowerCase().includes(search);
      return matchesFilter && matchesSearch;
    }

    return matchesFilter;
  });

  const handleViewMember = async (member: StoreClubMember) => {
    setSelectedMember(member);
    setActiveTab('details');
    setIsEditing(false);
    await loadMemberDetail(member.id);
  };

  const handleCloseMember = () => {
    setSelectedMember(null);
    setMemberDetail(null);
    setIsEditing(false);
    setNewPassword('');
  };

  const handleEnterEditMode = () => {
    if (!memberDetail) return;
    setEditFormData({
      name: memberDetail.name || '',
      email: memberDetail.email || '',
      phone: memberDetail.phone || '',
      street_address: memberDetail.street_address || '',
      city: memberDetail.city || '',
      state: memberDetail.state || '',
      zip_code: memberDetail.zip_code || '',
      reminder_days_before: memberDetail.reminder_days_before || 3,
      weather_alerts_enabled: memberDetail.weather_alerts_enabled ?? true,
      is_approved: memberDetail.is_approved ?? false,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewPassword('');
  };

  const handleSaveMember = async () => {
    if (!selectedMember) return;
    try {
      setIsSaving(true);
      await api.patch(`/admin/store-club-members/${selectedMember.id}/`, editFormData);

      // Reload data
      await loadMembers();
      await loadStats();
      await loadMemberDetail(selectedMember.id);

      // Update selected member with new data
      setSelectedMember(prev => prev ? { ...prev, ...editFormData } : null);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving member:', err);
      alert(err.response?.data?.error || 'Failed to save member');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (!selectedMember || !newPassword) return;
    try {
      setSettingPassword(true);
      await api.post(`/admin/store-club-members/${selectedMember.id}/set-password/`, {
        password: newPassword
      });
      alert('Password updated successfully');
      setNewPassword('');
    } catch (err: any) {
      console.error('Error setting password:', err);
      alert(err.response?.data?.error || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!selectedMember) return;
    if (!window.confirm(`Send password reset email to ${selectedMember.email}?`)) return;

    try {
      setSettingPassword(true);
      await api.post(`/admin/store-club-members/${selectedMember.id}/send-password-reset/`);
      alert('Password reset email sent');
    } catch (err: any) {
      console.error('Error sending password reset:', err);
      alert(err.response?.data?.error || 'Failed to send password reset email');
    } finally {
      setSettingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount == null || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleApprove = async (member: StoreClubMember) => {
    if (!window.confirm(`Approve ${member.name} as a Store Club member?`)) {
      return;
    }

    try {
      setProcessing(true);
      await api.post(`/admin/store-club-members/${member.id}/approve/`);
      await loadMembers();
      await loadStats();
      if (memberDetail) {
        await loadMemberDetail(member.id);
      }
      setSelectedMember(prev => prev ? { ...prev, is_approved: true } : null);
    } catch (err: any) {
      console.error('Error approving member:', err);
      alert(err.response?.data?.error || 'Failed to approve member');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (member: StoreClubMember) => {
    const reason = window.prompt(`Enter reason for removing ${member.name} (optional):`);
    if (reason === null) return;

    try {
      setProcessing(true);
      await api.post(`/admin/store-club-members/${member.id}/reject/`, { reason });
      await loadMembers();
      await loadStats();
      handleCloseMember();
    } catch (err: any) {
      console.error('Error rejecting member:', err);
      alert(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setProcessing(false);
    }
  };

  const getAnimalIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      dog: '🐕', cat: '🐱', chicken: '🐔', duck: '🦆', goat: '🐐',
      sheep: '🐑', pig: '🐷', cow: '🐄', horse: '🐴', rabbit: '🐰',
      turkey: '🦃', goose: '🪿', llama: '🦙', alpaca: '🦙',
    };
    return icons[type.toLowerCase()] || '🐾';
  };

  const getOrderStatusStyle = (status: string) => {
    switch (status) {
      case 'picked_up':
        return { backgroundColor: '#e5e7eb', color: '#374151' };
      case 'ready_for_pickup':
        return { backgroundColor: '#d1fae5', color: '#065f46' };
      case 'paid':
        return { backgroundColor: '#dbeafe', color: '#1e40af' };
      default:
        return { backgroundColor: '#fef3c7', color: '#92400e' };
    }
  };

  if (loading && members.length === 0) {
    return (
      <div className="store-club-members-page">
        <div className="loading">Loading Store Club members...</div>
      </div>
    );
  }

  return (
    <div className="store-club-members-page">
      {/* Legacy-page banner. StoreClubMember is the pre-migration model;
          new feature work targets BridgeClubMember. Page is kept until the
          data migration is fully verified. */}
      <div style={{
        background: '#FEF3C7',
        border: '1px solid #F59E0B',
        borderRadius: 8,
        padding: '12px 16px',
        margin: '0 0 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div>
          <div style={{ fontWeight: 600, color: '#92400E' }}>
            Legacy Page — Store Club Members
          </div>
          <div style={{ fontSize: 13, color: '#B45309' }}>
            These members have been migrated to Bridge Club. Use the Bridge
            Club admin section to manage local members. This page will be
            removed in a future update.
          </div>
        </div>
      </div>

      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1>Store Club Members</h1>
            <p className="page-subtitle">Manage retail Store Club memberships</p>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Clickable Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div
            className={`stat-card ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            <div className="stat-value">{stats.total_members}</div>
            <div className="stat-label">Total Members</div>
          </div>
          <div
            className={`stat-card ${activeFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveFilter('pending')}
          >
            <div className={`stat-value ${stats.pending_members > 0 ? 'pending' : ''}`}>
              {stats.pending_members}
            </div>
            <div className="stat-label">Pending Approval</div>
          </div>
          <div
            className={`stat-card ${activeFilter === 'new' ? 'active' : ''}`}
            onClick={() => setActiveFilter('new')}
          >
            <div className="stat-value">{stats.new_this_month}</div>
            <div className="stat-label">New This Month</div>
          </div>
          <div className="stat-card" style={{ cursor: 'default' }}>
            <div className="stat-value">{formatCurrency(stats.revenue_this_month)}</div>
            <div className="stat-label">Revenue (30 Days)</div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="filters-section">
        <div className="search-row">
          <input
            type="text"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, phone, or city..."
          />
          <div className="results-count">
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="members-list">
        {filteredMembers.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? 'No members match your search' :
             activeFilter === 'pending' ? 'No pending members' :
             activeFilter === 'new' ? 'No new members this month' :
             'No Store Club members found'}
          </div>
        ) : (
          filteredMembers.map((member) => (
            <div
              key={member.id}
              className="member-card"
              onClick={() => handleViewMember(member)}
            >
              <div className="member-card-header">
                <h3 className="member-name">{member.name}</h3>
                <span className={`member-status ${member.is_approved ? 'approved' : 'pending'}`}>
                  {member.is_approved ? 'Approved' : 'Pending'}
                </span>
              </div>
              <div className="member-contact">
                <span className="member-email">{member.email}</span>
                <span className="member-phone">{formatPhone(member.phone)}</span>
              </div>
              <div className="member-meta">
                <span>📍 {member.city}, {member.state}</span>
                {member.distance_miles && <span>🚗 {member.distance_miles} mi</span>}
                <span>🐾 {member.animal_count} animals</span>
                <span>📦 {member.order_count} orders</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Full-Screen Member Detail View */}
      {selectedMember && (
        <div className="member-fullscreen-view">
          <div className="member-view-header">
            <div className="member-header-left">
              <h1>{selectedMember.name}</h1>
              <div className="member-header-status">
                {selectedMember.is_approved ? '✓ Approved Member' : '⏳ Pending Approval'}
              </div>
            </div>
            <div className="header-actions">
              {isEditing ? (
                <>
                  <button className="btn-cancel" onClick={handleCancelEdit}>Cancel</button>
                  <button
                    className="btn-save"
                    onClick={handleSaveMember}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button className="btn-edit" onClick={handleEnterEditMode}>Edit</button>
              )}
              <button className="btn-close" onClick={handleCloseMember}>✕</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="member-view-tabs">
            <button
              className={activeTab === 'details' ? 'active' : ''}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={activeTab === 'animals' ? 'active' : ''}
              onClick={() => setActiveTab('animals')}
            >
              Animals
              <span className="tab-count">{memberDetail?.animals?.length || 0}</span>
            </button>
            <button
              className={activeTab === 'orders' ? 'active' : ''}
              onClick={() => setActiveTab('orders')}
            >
              Orders
              <span className="tab-count">{memberDetail?.orders?.length || 0}</span>
            </button>
          </div>

          <div className="member-view-content">
            {activeTab === 'details' && !isEditing && (
              <>
                <div className="detail-section">
                  <h3>Contact Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Email</label>
                      <span>{selectedMember.email}</span>
                    </div>
                    <div className="detail-item">
                      <label>Phone</label>
                      <span>{formatPhone(selectedMember.phone)}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Address</h3>
                  <div className="address-block">
                    {memberDetail?.street_address && <div>{memberDetail.street_address}</div>}
                    <div>{selectedMember.city}, {selectedMember.state} {memberDetail?.zip_code}</div>
                    {selectedMember.distance_miles && (
                      <div style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                        {selectedMember.distance_miles} miles from store
                        {memberDetail?.drive_time_minutes && ` (~${memberDetail.drive_time_minutes} min drive)`}
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Preferences</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Feed Reminders</label>
                      <span>{memberDetail?.reminder_days_before || 3} days before</span>
                    </div>
                    <div className="detail-item">
                      <label>Weather Alerts</label>
                      <span>{memberDetail?.weather_alerts_enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Activity</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Member Since</label>
                      <span>{formatDate(selectedMember.joined_date)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Total Orders</label>
                      <span>{selectedMember.order_count}</span>
                    </div>
                    <div className="detail-item">
                      <label>Total Spent</label>
                      <span>{formatCurrency(selectedMember.total_spent)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'details' && isEditing && (
              <div className="edit-form">
                <div className="form-section">
                  <h3>Contact Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={editFormData.phone}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Address</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Street Address</label>
                      <input
                        type="text"
                        value={editFormData.street_address}
                        onChange={(e) => setEditFormData({ ...editFormData, street_address: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={editFormData.city}
                        onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={editFormData.state}
                        onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        value={editFormData.zip_code}
                        onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Preferences</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Feed Reminder Days</label>
                      <select
                        value={editFormData.reminder_days_before}
                        onChange={(e) => setEditFormData({ ...editFormData, reminder_days_before: parseInt(e.target.value) })}
                      >
                        <option value={1}>1 day before</option>
                        <option value={2}>2 days before</option>
                        <option value={3}>3 days before</option>
                        <option value={5}>5 days before</option>
                        <option value={7}>7 days before</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Weather Alerts</label>
                      <select
                        value={editFormData.weather_alerts_enabled ? 'true' : 'false'}
                        onChange={(e) => setEditFormData({ ...editFormData, weather_alerts_enabled: e.target.value === 'true' })}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* TODO: Future Enhancement - Voice Notes
                 * Add a "Staff Notes" section here with microphone button
                 * Staff can tap microphone and speak notes about the member
                 * AI (Whisper API) transcribes speech to text
                 * Auto-saves to member notes with timestamp
                 * Example: "Customer is getting 10 more chickens next month"
                 * See /docs/FUTURE_FEATURES.md for implementation details
                 */}

                <div className="form-section">
                  <h3>Account Access</h3>
                  <div className="password-section">
                    <h4>Password Management</h4>
                    <div className="password-actions">
                      <button
                        className="btn-reset-password"
                        onClick={handleSendPasswordReset}
                        disabled={settingPassword}
                      >
                        {settingPassword ? 'Sending...' : 'Send Password Reset Email'}
                      </button>
                      <div className="password-input-group">
                        <PasswordInput
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Set new password..."
                        />
                        <button
                          className="btn-set-password"
                          onClick={handleSetPassword}
                          disabled={!newPassword || settingPassword}
                        >
                          {settingPassword ? 'Setting...' : 'Set Password'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'animals' && (
              <div className="animals-list">
                {memberDetail?.animals && memberDetail.animals.length > 0 ? (
                  memberDetail.animals.map((animal) => (
                    <div key={animal.id} className="animal-card">
                      <div className="animal-icon">{getAnimalIcon(animal.animal_type)}</div>
                      <div className="animal-info">
                        <h4 className="animal-name">{animal.name || animal.animal_type}</h4>
                        <div className="animal-details">
                          {animal.breed && <span>{animal.breed} • </span>}
                          {animal.count > 1 ? `${animal.count} animals` : '1 animal'}
                          {animal.weight_lbs && ` • ${animal.weight_lbs} lbs`}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No animals registered</div>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <>
                {memberDetail?.orders && memberDetail.orders.length > 0 ? (
                  <div className="orders-list">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Pickup</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberDetail.orders.map((order) => (
                          <tr key={order.id}>
                            <td>{order.order_number}</td>
                            <td>{formatDate(order.order_date)}</td>
                            <td>
                              <span
                                className="order-status-badge"
                                style={getOrderStatusStyle(order.status)}
                              >
                                {order.status_display || order.status}
                              </span>
                            </td>
                            <td>
                              {order.pickup_method === 'locker' ? '🔐 Locker' : '🏪 Store'}
                            </td>
                            <td>{formatCurrency(order.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">No orders yet</div>
                )}
              </>
            )}
          </div>

          {/* Footer Actions (only show when not editing) */}
          {!isEditing && (
            <div className="member-view-footer">
              {!selectedMember.is_approved && (
                <button
                  className="btn-approve"
                  onClick={() => handleApprove(selectedMember)}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Approve Member'}
                </button>
              )}
              <button
                className="btn-remove"
                onClick={() => handleReject(selectedMember)}
                disabled={processing}
              >
                Remove Member
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreClubMembers;
